// Jul-2026 — TDS Recovery Report
// One row per Sales Invoice where TDS is involved (invoice TDS or TDS the
// client actually deducted in Receipt Vouchers). Chase pending TDS
// certificates: mark received with certificate no/date, filter Pending only.
// Endpoints: POST /api/reports/tds-recovery/summary | /export.xlsx
//            POST /api/tds-certificates | DELETE /api/tds-certificates/{id}

import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import { BadgeCheck, FileSpreadsheet, Filter, ReceiptText, RotateCw, Undo2 } from "lucide-react";

const EMPTY = { from_date: "", to_date: "", branch_id: "", client_id: "", cert_status: "" };
const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const lblCls = "text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1";

export default function TdsRecoveryReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [marking, setMarking] = useState(null); // row being marked received

  useEffect(() => {
    (async () => {
      try {
        const [b, c] = await Promise.all([api.get("/branches"), api.get("/clients", { params: { limit: 500 } })]);
        setBranches(Array.isArray(b.data) ? b.data : b.data?.items || []);
        setClients(Array.isArray(c.data) ? c.data : c.data?.items || []);
      } catch { /* dropdowns optional */ }
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(f = filters) {
    setLoading(true);
    try {
      const res = await api.post("/reports/tds-recovery/summary", f);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load the report");
    } finally { setLoading(false); }
  }

  async function doExport() {
    setExporting(true);
    try {
      const res = await api.post("/reports/tds-recovery/export.xlsx", filters, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tds_recovery_report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel export failed");
    } finally { setExporting(false); }
  }

  async function unmark(row) {
    try {
      await api.delete(`/tds-certificates/${row.invoice_id}`);
      toast.success(`${row.invoice_number} — certificate mark removed`);
      run();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to undo");
    }
  }

  const rows = data?.rows || [];
  const pg = useClientPagination(rows, { initialPageSize: 50 });
  const t = data?.totals || {};
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const cards = [
    { key: "invoices", label: "Invoices", tone: "border-slate-300 bg-slate-100 text-slate-900" },
    { key: "invoice_tds", label: "Invoice TDS", tone: "border-sky-200 bg-sky-50 text-sky-900", money: true },
    { key: "receipt_tds", label: "TDS in Receipts", tone: "border-violet-200 bg-violet-50 text-violet-900", money: true },
    { key: "pending_tds", label: `Pending Certificates (${t.pending_count ?? 0})`, tone: "border-rose-200 bg-rose-50 text-rose-900", money: true },
    { key: "received_tds", label: `Received (${t.received_count ?? 0})`, tone: "border-emerald-200 bg-emerald-50 text-emerald-900", money: true },
  ];

  return (
    <div data-testid="tds-recovery-page">
      <PageHeader
        eyebrow="Receivables"
        title="TDS Recovery Report"
        subtitle="TDS deducted by clients against Sales Invoices — expected (invoice) vs actually deducted (receipts) — with TDS certificate (Form 16A) tracking so pending certificates are easy to chase."
        actions={
          <button data-testid="tdsr-export-xlsx" disabled={exporting || loading} onClick={doExport}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <FileSpreadsheet size={14} /> {exporting ? "Exporting…" : "Excel"}
          </button>
        }
      />

      <Card className="mb-4">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2"><Filter size={12} /> Filters</h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div><div className={lblCls}>From Date</div>
            <input data-testid="tdsr-from" type="date" value={filters.from_date} onChange={(e) => set("from_date", e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>To Date</div>
            <input data-testid="tdsr-to" type="date" value={filters.to_date} onChange={(e) => set("to_date", e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>Branch</div>
            <select data-testid="tdsr-branch" value={filters.branch_id} onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
              <option value="">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select></div>
          <div><div className={lblCls}>Client</div>
            <SearchableSelect
              value={filters.client_id}
              onChange={(id) => set("client_id", id)}
              items={clients}
              getLabel={(c) => c.name || ""}
              placeholder="All Clients"
              searchPlaceholder="Search client…"
              testid="tdsr-client"
            /></div>
          <div><div className={lblCls}>Certificate</div>
            <select data-testid="tdsr-cert-status" value={filters.cert_status} onChange={(e) => set("cert_status", e.target.value)} className={selCls}>
              <option value="">All</option>
              <option value="Pending">Pending Only</option>
              <option value="Received">Received Only</option>
            </select></div>
          <div className="flex gap-2">
            <button data-testid="tdsr-apply" onClick={() => run()}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
            </button>
            <button data-testid="tdsr-reset" onClick={() => { setFilters(EMPTY); run(EMPTY); }}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Reset</button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`tdsr-card-${c.key}`} className={`border px-4 py-3 ${c.tone}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{c.label}</div>
            <div className="text-xl font-bold mt-0.5">{c.money ? formatINR(t[c.key] ?? 0) : (t[c.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      {t.truncated && (
        <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Scanned the first 3,000 invoices — narrow the date range to see everything.
        </div>
      )}

      <Card>
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <ReceiptText size={14} className="text-orange-600" /> TDS by Invoice ({rows.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="tds-recovery-table" className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                {["Invoice No", "Date", "FY / Qtr", "Client", "Project", "Branch"].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-bold">{h}</th>
                ))}
                <th className="px-3 py-2.5 font-bold text-right">Invoice TDS</th>
                <th className="px-3 py-2.5 font-bold text-right">TDS in Receipts</th>
                <th className="px-3 py-2.5 font-bold">Receipt Vouchers</th>
                <th className="px-3 py-2.5 font-bold">Certificate</th>
                <th className="px-3 py-2.5 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-400">No TDS-bearing invoices match these filters.</td></tr>
              ) : (
                pg.paged.map((r, i) => (
                  <tr key={r.invoice_id} data-testid={`tdsr-row-${i}`} className="border-t border-slate-100 hover:bg-orange-50/40">
                    <td className="px-3 py-2 whitespace-nowrap font-bold text-slate-900">{r.invoice_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.invoice_date}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.fy} · {r.quarter}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{r.project_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.branch_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.invoice_tds)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{r.receipt_tds ? formatINR(r.receipt_tds) : "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-600 max-w-[220px]">{r.receipt_refs || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.cert_status === "Received" ? (
                        <span className="inline-flex items-center gap-1 border border-emerald-200 bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                          <BadgeCheck size={11} /> Received{r.certificate_no ? ` · ${r.certificate_no}` : ""}
                        </span>
                      ) : (
                        <span className="inline-block border border-rose-200 bg-rose-100 text-rose-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Pending</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.cert_status === "Received" ? (
                        <button data-testid={`tdsr-undo-${i}`} onClick={() => unmark(r)}
                          className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-slate-50">
                          <Undo2 size={11} /> Undo
                        </button>
                      ) : (
                        <button data-testid={`tdsr-mark-${i}`} onClick={() => setMarking({ ...r, certificate_no: "", certificate_date: "", remarks: "" })}
                          className="inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
                          <BadgeCheck size={11} /> Mark Received
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-slate-100">
          <PaginationControls page={pg.page} pageSize={pg.pageSize} total={pg.total}
            onPage={pg.setPage} onPageSize={pg.setPageSize} pageSizeOptions={[25, 50, 100, 200]} />
        </div>
      </Card>

      {marking && (
        <MarkReceivedModal row={marking} onClose={() => setMarking(null)}
          onSaved={() => { setMarking(null); run(); }} />
      )}
    </div>
  );
}

function MarkReceivedModal({ row, onClose, onSaved }) {
  const [certNo, setCertNo] = useState("");
  const [certDate, setCertDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.post("/tds-certificates", {
        invoice_id: row.invoice_id,
        certificate_no: certNo,
        certificate_date: certDate,
        remarks,
      });
      toast.success(`${row.invoice_number} — TDS certificate marked received`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark received");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="tdsr-mark-modal">
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Mark TDS Certificate Received</div>
          <div className="text-sm font-bold text-slate-900 mt-0.5">{row.invoice_number} · {row.client_name}</div>
        </div>
        <div className="p-5 space-y-3">
          <div><div className={lblCls}>Certificate No (Form 16A)</div>
            <input data-testid="tdsr-cert-no" value={certNo} onChange={(e) => setCertNo(e.target.value)}
              placeholder="e.g. ABCD1234" className={selCls} /></div>
          <div><div className={lblCls}>Certificate Date</div>
            <input data-testid="tdsr-cert-date" type="date" value={certDate} onChange={(e) => setCertDate(e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>Remarks (optional)</div>
            <input data-testid="tdsr-cert-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. received by email from client" className={selCls} /></div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button data-testid="tdsr-mark-cancel" onClick={onClose}
            className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button data-testid="tdsr-mark-save" disabled={saving} onClick={save}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
            {saving ? "Saving…" : "Mark Received"}
          </button>
        </div>
      </div>
    </div>
  );
}
